<template>
  <WelcomeBackLoginModal
    v-if="isLoginModalOpen"
    :email="prompt.email"
    :forgot-password-url="loginModalConfig.forgotPassword"
    :magic-link-redirect-url="prompt.redirectUrl"
    :tracking-params="store.getters.authEventsTrackingParams('email_password')"
    :api-config="loginModalConfig.apiConfig"
    :google-api-config="loginModalConfig.googleApiConfig"
    :apple-api-config="loginModalConfig.appleApiConfig"
    top-text="Log in to your existing account."
    @login="onLogin"
    @error="onLoginError"
    @close="dismiss"
    @trackEvent="trackGa4Interaction"
  />

  <BaseModal
    v-else
    is-open
    size="small"
    :show-close-button="false"
    @close="dismiss"
  >
    <div class="flex items-center gap-4 bg-green-50 rounded-2xl px-4 py-6">
      <div class="flex items-center justify-center shrink-0 bg-green-100 rounded-full p-3">
        <CheckCircleIcon class="w-8 h-8 text-green" />
      </div>
      <div>
        <div class="text-init font-semibold mb-2">
          Your enquiry was sent!
        </div>
        <div class="text-sm leading-5">
          The venue manager will get back to you shortly.
        </div>
      </div>
    </div>

    <HorizontalLine class="mt-6" />

    <div class="text-xl font-semibold text-center mb-4 mt-8">
      Welcome back
    </div>

    <div class="bg-grey-50 rounded-lg p-4 mb-2 text-sm font-semibold text-center truncate" v-text="prompt.email" />

    <div class="text-sm leading-5 text-center mt-6 mb-4">
      We found an account linked to this email. Log in to view and manage your enquiries.
    </div>

    <BaseButton color="green" class="w-full mt-4" @click="onLoginClick">
      Log in
    </BaseButton>
    <BaseButton color="text" class="w-full mt-4" @click="onNotNowClick">
      Not now
    </BaseButton>
  </BaseModal>
</template>

<script setup>
import CheckCircleIcon from '@tagvenue/icons/24/bold/general/check-circle.svg';
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';

import BaseButton from '@/components/Ui/Buttons/BaseButton.vue';
import HorizontalLine from '@/components/Ui/HorizontalLine.vue';
import BaseModal from '@/components/Ui/Modals/BaseModal.vue';
import WelcomeBackLoginModal from '@/modules/user-cabinet/user-cabinet/BookingForm/WelcomeBackLoginModal.vue';
import { useToaster } from '@/services/toaster';

const TRACKING_SECTION = 'enquiry_sent_welcome_back';

const store = useStore();
const { showNotification } = useToaster();

const isLoginModalOpen = ref(false);

const prompt = computed(() => store.state.enquirySentWelcomeBack);

// The room page exposes its social-login config for the wishlist modal; the same config drives the
// login modal here.
const loginModalConfig = window._VUE_WISHLIST_MODAL;

const trackGa4Interaction = ({ section, element, interaction }) => {
  store.dispatch('trackGA4Interaction', {
    section,
    element,
    interaction,
    value: null,
  });
};

const trackEvent = (element, interaction = 'click') => {
  trackGa4Interaction({ section: TRACKING_SECTION, element, interaction });
};

const dismiss = () => {
  store.commit('setLastEnquiry', prompt.value.lastEnquiry);
  store.commit('setLoading', { request: 'sendEnquiry', value: false });
  store.commit('setEnquirySentWelcomeBack', null);
};

const onLoginClick = () => {
  trackEvent('login_button');
  isLoginModalOpen.value = true;
};

const onNotNowClick = () => {
  trackEvent('not_now_button');
  dismiss();
};

const onLogin = ({ emailMatch }) => {
  // The enquiry belongs to the account behind the typed email; a different account would 404 on
  // its recommender page, so send that Client to their own enquiries instead.
  if (!emailMatch) {
    window.location = store.state.pages.enquiriesListView;
    return;
  }

  if (prompt.value.redirectUrl) {
    window.location = prompt.value.redirectUrl;
    return;
  }

  window.location.reload();
};

const onLoginError = (error) => {
  showNotification.error(error);
};

onMounted(() => {
  trackEvent('modal', 'visible');
});
</script>
